import axios from "axios";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
  withCredentials: true,
  timeout: 15000,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("fyz_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
}, (err) => Promise.reject(err));

// Paths that should NEVER trigger a forced logout even if they 401 —
// these are auth endpoints whose 401 just means "bad credentials".
const AUTH_PATH_PATTERNS = [
  /\/auth\/login\b/,
  /\/auth\/register\b/,
  /\/auth\/verify-otp\b/,
  /\/auth\/send-otp\b/,
  /\/auth\/resend-otp\b/,
  /\/auth\/forgot-password\b/,
  /\/auth\/reset-password\b/,
  /\/auth\/social\b/,
];

function isAuthEndpoint(url = "") {
  return AUTH_PATH_PATTERNS.some((re) => re.test(url));
}

// Routes that are publicly accessible; no need to redirect to /login
// when the session simply expired and we're already here.
const PUBLIC_ROUTES = new Set([
  "/",
  "/salons",
  "/shop",
  "/academy",
  "/ai",
  "/membership",
  "/login",
  "/register",
  "/reset-password",
  "/verify-email",
  // Marketing surface
  "/about",
  "/how-it-works",
  "/partners",
  "/for-partners",
  "/for-stylists",
  "/stylists",
  "/careers",
  "/contact",
  "/faq",
  "/help",
  "/privacy",
  "/terms",
  "/refund-policy",
  "/cancellation-policy",
]);

function isPublicRoute(pathname = "") {
  if (PUBLIC_ROUTES.has(pathname)) return true;
  if (pathname.startsWith("/salons/") || pathname.startsWith("/stylists/")) return true;
  return false;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status   = err.response?.status;
    const message  = err.response?.data?.message || "Something went wrong.";
    const reqUrl   = err.config?.url || "";

    if (status === 401 && !isAuthEndpoint(reqUrl)) {
      const hadToken = !!localStorage.getItem("fyz_token");
      localStorage.removeItem("fyz_token");
      localStorage.removeItem("fyz_user");
      window.dispatchEvent(new Event("fyz:logout"));

      // If we had a session and aren't already on a public page, send
      // the user to /login with a returnTo. Use pushState so it doesn't
      // hard-reload the SPA.
      if (hadToken && typeof window !== "undefined") {
        const here = window.location.pathname + window.location.search;
        if (!isPublicRoute(window.location.pathname)) {
          const next = `/login?next=${encodeURIComponent(here)}`;
          if (window.location.pathname !== "/login") {
            window.history.pushState({}, "", next);
            window.dispatchEvent(new PopStateEvent("popstate"));
          }
        }
      }
    }

    return Promise.reject({
      status,
      message,
      errors: err.response?.data?.errors || {},
      data:   err.response?.data || null,
      response: err.response, // Backward compatibility for pages reading err.response?.data?.message
    });
  }
);

export default api;
